import { Component, Inject, OnInit, OnDestroy, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { FileUploadProgress, FileUploadFileStatus } from 'src/app/models/file-upload/file-upload-progress';
import { FileUploadStandaloneService } from 'src/app/services/file-upload-standalone/file-upload-standalone.service';
import { TranslationsProviderService } from 'src/app/services/translations-provider/translations-provider.service';

const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'tiff', 'tif'];
const VIDEO_EXTS = ['mp4', 'webm', 'avi', 'mov', 'mkv'];
const PDF_EXTS = ['pdf'];

// Fallback English translations (until BE is rebuilt with new keys)
const FALLBACKS: Record<string, string> = {
  UploadProgress: 'Upload Progress',
  UploadingNItems: 'Uploading {0} items...',
  UploadComplete: 'Upload complete!',
  FilesCompleted: 'files completed',
  Pending: 'Pending',
};

@Component({
  selector: 'app-sem-upload-progress-dialog',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './sem-upload-progress-dialog.component.html',
})
export class SemUploadProgressDialogComponent implements OnInit, OnDestroy {

  progress: FileUploadProgress = {
    totalFiles: 0, completedFiles: 0, overallPercent: 0, files: [], state: 'idle'
  };

  ringStyle: SafeStyle;

  @ViewChild('fileList') fileListEl: ElementRef<HTMLElement>;

  private sub: Subscription | null = null;

  constructor(
    public dialogRef: MatDialogRef<SemUploadProgressDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { service: FileUploadStandaloneService },
    public tps: TranslationsProviderService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) {
    this.ringStyle = this.buildRingStyle(0);
  }

  ngOnInit(): void {
    this.dialogRef.updateSize('420px', 'auto');

    this.sub = this.data.service.progress$.subscribe(p => {
      this.progress = p;
      this.ringStyle = this.buildRingStyle(p.overallPercent);
      this.cdr.detectChanges();
      this.scrollToActiveFile();
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  t(key: string): string {
    const val = this.tps.translate(key);
    return (val && val !== key) ? val : (FALLBACKS[key] || key);
  }

  /** Return file type category from filename for icon selection */
  getFileType(name: string): 'image' | 'video' | 'pdf' | 'file' {
    const ext = (name || '').split('.').pop()?.toLowerCase() || '';
    if (IMAGE_EXTS.includes(ext)) return 'image';
    if (VIDEO_EXTS.includes(ext)) return 'video';
    if (PDF_EXTS.includes(ext)) return 'pdf';
    return 'file';
  }

  private buildRingStyle(pct: number): SafeStyle {
    return this.sanitizer.bypassSecurityTrustStyle(
      `background: conic-gradient(var(--wg-accent, #3b82f6) ${pct}%, #e2e8f0 ${pct}%);` +
      `-webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 10px), #000 calc(100% - 9px));` +
      `mask: radial-gradient(farthest-side, transparent calc(100% - 10px), #000 calc(100% - 9px));` +
      `transition: background 0.4s ease;`
    );
  }

  private scrollToActiveFile(): void {
    const container = this.fileListEl?.nativeElement;
    if (!container) return;
    const active = container.querySelector('.upd__file--uploading') as HTMLElement;
    if (active) {
      active.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  closeDialog(): void {
    this.dialogRef.close();
  }
}
